import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useChat } from './useChat';
import * as S from './styles';

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    messages,
    isLoading,
    sessionId,
    error,
    initSession,
    sendMessage,
    resetChat,
  } = useChat();

  // Auto-init session when panel opens
  useEffect(() => {
    if (isOpen && !sessionId) {
      initSession();
    }
  }, [isOpen, sessionId, initSession]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue('');
    sendMessage('text', text);
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleButtonClick = useCallback(
    (value: string) => {
      if (isLoading) return;
      sendMessage('button', value);
    },
    [isLoading, sendMessage],
  );

  const handleReset = useCallback(() => {
    resetChat();
    initSession();
  }, [resetChat, initSession]);

  /* ── Closed state: trigger button ── */
  if (!isOpen) {
    return (
      <button
        type="button"
        css={S.triggerButton}
        onClick={() => setIsOpen(true)}
        aria-label="고객 상담 열기"
      >
        💬
      </button>
    );
  }

  /* ── Open state: chat panel ── */
  return (
    <div css={S.panel}>
      {/* Header */}
      <div css={S.header}>
        <span css={S.headerTitle}>
          🎡 리얼월드 고객상담
        </span>
        <div css={S.headerActions}>
          <button type="button" css={S.headerBtn} onClick={handleReset} title="새 대화">
            ↻
          </button>
          <button type="button" css={S.headerBtn} onClick={() => setIsOpen(false)} title="닫기">
            ✕
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div css={S.errorBanner}>{error}</div>}

      {/* Messages */}
      <div css={S.messagesArea}>
        {messages.map((msg) => (
          <React.Fragment key={msg.id ?? `${msg.role}-${msg.content}-${msg.createdAt ?? ''}`}>
            <div css={S.messageBubble(msg.role === 'user')}>
              {msg.content}
            </div>
            {msg.buttons && msg.buttons.length > 0 && (
              <div css={S.buttonsContainer}>
                {msg.buttons.map((btn) => (
                  <button
                    type="button"
                    key={btn.value}
                    css={S.optionButton}
                    onClick={() => handleButtonClick(btn.value)}
                    disabled={isLoading}
                  >
                    {btn.label}
                  </button>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
        {isLoading && (
          <div css={S.loadingDots}>
            <span />
            <span />
            <span />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div css={S.inputArea}>
        <textarea
          ref={inputRef}
          css={S.textInput}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="질문을 입력하세요..."
          rows={1}
          disabled={isLoading}
        />
        <button
          type="button"
          css={S.sendBtn}
          onClick={handleSend}
          disabled={!inputValue.trim() || isLoading}
        >
          전송
        </button>
      </div>
    </div>
  );
}
